import axios from 'axios';

const API_BASE_URL = process.env.VUE_APP_ADMIN_URL;

export default {
    async getUserData(userId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/users/360/user-data/${userId}`,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async getPetData(userId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/pets/360/pet-data/${userId}`,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async getMembershipServicesData(userId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/memberships/360/membership-services-data/${userId}`,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async getMembershipData(userId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/memberships/360/membership-data/${userId}`,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    // Uso de servicios de una membresía (eventos/montos usados, topes y estado) para la
    // sección "Uso de servicios" del 360. Incluye usos en carencia.
    async getMembershipServiceUsage(membershipId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/memberships/360/${membershipId}/service-usage`,
            { headers: this._getAuthHeaders(token), timeout: 10000 }
        );
        return this._handleResponse(response);
    },

    // Resumen de uso de todas las membresías Activas del usuario en una sola llamada
    // (113-user-360-redesign): una fila por membresía, en la moneda de su plan.
    async getActiveMembershipsServiceUsage(pgId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/memberships/360/active-service-usage/${pgId}`,
            { headers: this._getAuthHeaders(token), timeout: 10000 }
        );
        return this._handleResponse(response);
    },

    async getRefundsData(userId, token, page = 1, limit = 10) {
        const response = await axios.get(
            `${API_BASE_URL}/refunds/360/refunds-data`,
            {
                params: { userId: userId, page, limit },
                headers: this._getAuthHeaders(token),
                timeout: 10000
            }
        );
        if (response.data && response.data.status === 'ok') {
            return { data: response.data.data, total: response.data.total };
        }
        throw new Error(response.data?.message || 'Error en la respuesta del servidor');
    },

    // Usos de cobertura que no son reembolso (operativo, PetMed, telemedicina, upsell, reembolsos
    // antiguos sin solicitud y usos sin tipo). Los reembolsos con solicitud ya vienen del historial.
    async getServiceUsage(userId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/services-records/360/${userId}`,
            { headers: this._getAuthHeaders(token), timeout: 10000 }
        );
        return this._handleResponse(response);
    },

    async getSponsorshipData(userId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/sponsorships/360/sponsorship-data/${userId}`,
            { headers: this._getAuthHeaders(token), timeout: 10000 }
        );
        return this._handleResponse(response);
    },

    async getPaymentsHistory(userId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/payments/${userId}`,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async resetUserPassword(userId, token) {
        const response = await axios.put(
            `${API_BASE_URL}/users/360/user/reset-password/`,
            { pgId: userId },
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async forceLogout(userId, token) {
        const response = await axios.post(
            `${API_BASE_URL}/users/360/force-logout/${userId}`,
            {},
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async updateMembershipStatus(membershipId, status, token, deactivationReason = null, sendEmail = true) {
        const body = { status, sendEmail };
        if (deactivationReason !== null && deactivationReason !== undefined) {
            body.deactivationReason = deactivationReason;
        }
        const response = await axios.put(
            `${API_BASE_URL}/memberships/change-membership-status/${membershipId}`,
            body,
            {
                headers: this._getAuthHeaders(token)
            }
        );
        return this._handleResponse(response);
    },

    async getMembershipStatusHistory(membershipId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/memberships/${membershipId}/status-history`,
            {
                headers: this._getAuthHeaders(token)
            }
        );
        return this._handleResponse(response);
    },

    async updateMembershipContract(membershipId, token) {
        const response = await axios.put(
            `${API_BASE_URL}/memberships/update-contract-by-template/${membershipId}`,
            {},
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async updateUserNote(userId, note, token) {
        const response = await axios.put(
            `${API_BASE_URL}/users/${userId}`,
            { note },
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    // Consulta si un usuario está en lista negra por RUT
    async getBlacklist(rut, token) {
        const response = await axios.get(
            `${API_BASE_URL}/users/360/blacklist`,
            {
                params: { rut },
                headers: this._getAuthHeaders(token)
            }
        );
        return this._handleResponse(response);
    },

    // Agrega un usuario a la lista negra
    async createBlacklist(rut, reason, token) {
        const response = await axios.post(
            `${API_BASE_URL}/users/360/blacklist`,
            { rut, reason },
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    // [NUEVO] Actualiza o desactiva un registro de lista negra
    async updateBlacklist(rut, reason, active, token) {
        const response = await axios.put(
            `${API_BASE_URL}/users/360/blacklist`,
            { rut, reason, active },
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async getAllCoTutors(token) {
        const response = await axios.get(
            `${API_BASE_URL}/users/360/co-tutors`,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async addCoTutor(userId, coTutorData, token) {
        const response = await axios.post(
            `${API_BASE_URL}/users/360/co-tutors/${userId}`,
            coTutorData,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async updateCoTutor(coTutorId, coTutorData, token) {
        const response = await axios.put(
            `${API_BASE_URL}/users/360/co-tutors/${coTutorId}`,
            coTutorData,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async getCoTutors(userId, token) {
        const response = await axios.get(
            `${API_BASE_URL}/users/360/co-tutors/${userId}`,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    async deleteCoTutor(coTutorId, token) {
        const response = await axios.delete(
            `${API_BASE_URL}/users/360/co-tutors/${coTutorId}`,
            { headers: this._getAuthHeaders(token) }
        );
        return this._handleResponse(response);
    },

    _getAuthHeaders(token) {
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    },

    _handleResponse(response) {
        if (Array.isArray(response.data)) return response.data;
        if (response.data && response.data.status === 'ok') return response.data.data;
        throw new Error(response.data?.message || 'Error en la respuesta del servidor');
    }
};